import React from "react";
import { Upload, message, Button } from "antd";
import ArrowUpIcon from "../../../assets/images/icons/arrow_up.svg";
import { CloseRounded } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { toggleTour } from "../../../reducers/tourSlice";
import JSZip from "jszip";

const { Dragger } = Upload;

const UploadMedia = ({
  handleFileSelect,
  fileList,
  handelClick,
  disabled,
}) => {
  const dispatch = useDispatch();

  /**
   * Validate actual MP3 file content
   */
  const isValidMp3File = async (file) => {
    try {
      const buffer = await file.slice(0, 10).arrayBuffer();
      const bytes = new Uint8Array(buffer);

      // ID3 header
      const isID3 =
        bytes[0] === 0x49 &&
        bytes[1] === 0x44 &&
        bytes[2] === 0x33;

      // MPEG frame sync
      const isMpegFrame =
        bytes[0] === 0xff &&
        (bytes[1] === 0xfb ||
          bytes[1] === 0xf3 ||
          bytes[1] === 0xf2);

      return isID3 || isMpegFrame;
    } catch (error) {
      console.error("MP3 validation error:", error);
      return false;
    }
  };

  /**
   * Validate actual ZIP file content
   */
  const isValidZipFile = async (file) => {
    try {
      const zip = await JSZip.loadAsync(file);

      const files = Object.values(zip.files);

      // Remove folders
      const actualFiles = files.filter((f) => !f.dir);

      if (actualFiles.length === 0) {
        message.error("ZIP file is empty.");
        return false;
      }

      for (const zipFile of actualFiles) {
        const fileName = zipFile.name.toLowerCase();

        // Extension validation
        if (!fileName.endsWith(".mp3")) {
          message.error(
            `ZIP file can contain only MP3 files. Invalid file found: ${zipFile.name}`
          );
          return false;
        }

        // Read first few bytes of file
        const buffer = await zipFile.async("uint8array");

        if (buffer.length < 3) {
          message.error(`Invalid MP3 file found: ${zipFile.name}`);
          return false;
        }

        // MP3 signature validation
        const isID3 =
          buffer[0] === 0x49 &&
          buffer[1] === 0x44 &&
          buffer[2] === 0x33;

        const isMpegFrame =
          buffer[0] === 0xff &&
          (buffer[1] === 0xfb ||
            buffer[1] === 0xf3 ||
            buffer[1] === 0xf2);

        if (!isID3 && !isMpegFrame) {
          message.error(
            `Invalid MP3 content found in ZIP: ${zipFile.name}`
          );
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error(error);
      message.error("Invalid ZIP file.");
      return false;
    }
  };

  /**
   * Validate File Before Upload
   */
  const beforeUpload = async (file) => {
    const expectedExtensions = [".mp3", ".zip"];

    const expectedMimeTypes = [
      "audio/mpeg",
      "application/zip",
      "application/x-zip-compressed",
      "multipart/x-zip",
    ];

    const fileExtension = file.name
      .slice(file.name.lastIndexOf("."))
      .toLowerCase();

    /**
     * Validate extension
     */
    if (!expectedExtensions.includes(fileExtension)) {
      message.error(
        "Invalid file format. Only .mp3 and .zip files are allowed."
      );
      return Upload.LIST_IGNORE;
    }

    /**
     * Validate MIME type (basic browser validation)
     */
    if (
      file.type &&
      !expectedMimeTypes.includes(file.type)
    ) {
      message.error(
        "Invalid file format. Only .mp3 and .zip files are allowed."
      );
      return Upload.LIST_IGNORE;
    }

    /**
     * Validate empty file
     */
    if (file.size === 0) {
      message.error(
        fileExtension === ".zip"
          ? "ZIP file cannot be empty."
          : "MP3 file cannot be empty."
      );
      return Upload.LIST_IGNORE;
    }

    /**
     * Validate filename characters
     */
    const validFilenameRegex =
      fileExtension === ".mp3"
        ? /^[a-zA-Z0-9 &'()_-]+\.mp3$/i
        : /^[a-zA-Z0-9 &'()_-]+\.zip$/i;

    if (!validFilenameRegex.test(file.name)) {
      message.error(
        "Invalid file name. Only letters, numbers, spaces, apostrophes ('), parentheses (), hyphens (-), underscores (_), and ampersands (&) are allowed."
      );

      handleFileSelect([]);
      return Upload.LIST_IGNORE;
    }

    /**
     * Validate size (Max 200MB)
     */
    const isLt200MB = file.size / 1024 / 1024 <= 200;

    if (!isLt200MB) {
      message.error(
        "File size exceeds 200MB. Please upload a smaller file."
      );
      return Upload.LIST_IGNORE;
    }

    /**
     * Prevent duplicate file
     */
    const isDuplicate = fileList.some(
      (existingFile) => existingFile.name === file.name
    );

    if (isDuplicate) {
      message.warning("File already selected.");
      return Upload.LIST_IGNORE;
    }

    /**
     * Validate actual file content (magic bytes)
     */
    if (fileExtension === ".mp3") {
      const isRealMp3 = await isValidMp3File(file);

      if (!isRealMp3) {
        message.error(
          "Invalid MP3 file. The file content does not match a valid MP3 format."
        );
        return Upload.LIST_IGNORE;
      }
    }

    if (fileExtension === ".zip") {
      const isRealZip = await isValidZipFile(file);

      if (!isRealZip) {
        message.error(
          "Invalid ZIP file. The file content does not match a valid ZIP format."
        );
        return Upload.LIST_IGNORE;
      }
    }

    return true;
  };

  /**
   * Handle File Change
   */
  const handleFileChange = (file) => {
    const data = file.fileList.map((item) =>
      item.originFileObj ? item.originFileObj : item
    );

    handleFileSelect(data);
  };

  /**
   * Dummy Upload Request
   */
  const customRequest = ({ file, onSuccess }) => {
    if (file && file.name) {
      setTimeout(() => {
        onSuccess("ok");
      }, 0);
    }
  };

  /**
   * Remove File
   */
  const onRemove = () => {
    handleFileSelect([]);
  };

  /**
   * Disable Tour When Clicking Upload
   */
  const handleClickOnDragger = () => {
    dispatch(toggleTour({ show: false }));
  };

  return (
    <Dragger
      className="pcvc-upload"
      fileList={fileList}
      multiple={false}
      beforeUpload={beforeUpload}
      onChange={handleFileChange}
      customRequest={customRequest}
      showUploadList={false}
      maxCount={1}
      onClick={handleClickOnDragger}
      disabled={fileList.length > 0}
      accept=".mp3,.zip"
    >
      <div className="upload-container">
        {fileList.length === 0 ? (
          <div className="upload-action">
            <div className="title">Drag and drop here or</div>

            <button className="btn-outlined">
              <div className="btn-outlined-content">
                <div className="text">Upload files</div>
                <img width={29} src={ArrowUpIcon} alt="upload" />
              </div>
            </button>

            <div
              style={{
                marginTop: "2px",
                fontSize: "12px",
                color: "#8c8c8c",
                textAlign: "center",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
            >
              Upload MP3 or ZIP files up to 200MB.
            </div>
          </div>
        ) : (
          <div
            className="upload-action"
            style={{ zIndex: 9999 }}
            onClick={() => {
              return;
            }}
          >
            <div className="selectedList">
              <div className="name">{fileList[0].name}</div>
              <CloseRounded className="remove" onClick={onRemove} />
            </div>
          </div>
        )}
      </div>

      <div className="actions">
        <Button type="primary" onClick={handelClick} disabled={disabled}>
          Submit
        </Button>
      </div>
    </Dragger>
  );
};

export default UploadMedia;